import { Wrench, Truck, Scissors, ShoppingCart, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/constants/content";

function Num({ n, tone = "text-brandsky-deep" }) {
  return <span className={`font-mono text-sm font-bold tracking-widest ${tone}`}>{n}</span>;
}

export default function Services() {
  return (
    <section id="services" className="relative py-20 sm:py-28" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 grid gap-6 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow text-brandsky-deep">What we do</p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
              Every handpiece, every problem.
              <br />
              One phone call.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="max-w-md text-[15.5px] leading-relaxed text-slate-600 lg:ml-auto">
              From latch heads to electric micro-motors — if it spins, sprays or scales, we fix it. And when the van
              can't come to your door, your handpiece is back to spec within 24–72 hours of reaching our bench.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div
              data-testid="service-card-handpiece-repair"
              className="group relative flex min-h-[340px] flex-col justify-end overflow-hidden rounded-[22px] shadow-[0_10px_36px_rgba(22,50,79,0.14)]"
            >
              <img
                src={IMAGES.workshop}
                alt="Handpiece repair work in progress"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-abyss/95 via-abyss/55 to-abyss/10" />
              <div className="relative p-7 sm:p-9">
                <div className="flex items-center gap-3">
                  <Num n="01" tone="text-brandsky" />
                  <Wrench className="h-4 w-4 text-brandsky" />
                </div>
                <h3 className="font-display mt-3 text-2xl font-bold text-white sm:text-[1.7rem]">Handpiece repair</h3>
                <p className="mt-2 max-w-md text-[14.5px] leading-relaxed text-white/75">
                  Pneumatics, electrics, high/low-speed, latch heads and nosecones. New bearings, o-rings, gaskets,
                  turbines and chucks — parts built to meet or exceed OEM spec.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["High-speed", "Low-speed", "Electric", "Pneumatic", "Nosecones"].map((t) => (
                    <span key={t} className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11.5px] font-semibold text-white/90 backdrop-blur">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="lg:col-span-5">
            <div
              data-testid="service-card-mobile-van"
              className="group relative flex h-full flex-col justify-between overflow-hidden rounded-[22px] border border-brandsky/30 bg-brandsky-soft p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_56px_rgba(59,191,230,0.25)] sm:p-9"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brandsky/20 blur-2xl transition-transform duration-700 group-hover:scale-125" />
              <div>
                <div className="flex items-center gap-3">
                  <Num n="02" />
                  <Truck className="h-4 w-4 text-brandsky-deep" />
                </div>
                <h3 className="font-display mt-3 text-2xl font-bold text-ink">On-site mobile van</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">
                  We park at your office and repair equipment right in the parking lot. Parts on board means back in
                  your hands <strong className="font-semibold text-ink">the same day — usually within about an hour.</strong>
                </p>
              </div>
              <a href="#van" className="link-underline mt-6 w-fit inline-flex items-center gap-1.5 text-[13.5px] font-bold text-brandsky-deep">
                See how the van works <span aria-hidden>→</span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.05} className="lg:col-span-5">
            <div
              data-testid="service-card-sharpening"
              className="group relative flex min-h-[300px] flex-col justify-end overflow-hidden rounded-[22px] shadow-[0_10px_36px_rgba(22,50,79,0.14)]"
            >
              <img
                src={IMAGES.sharpening}
                alt="Dental instruments laid out for sharpening"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-abyss/95 via-abyss/50 to-transparent" />
              <div className="relative p-7 sm:p-8">
                <div className="flex items-center gap-3">
                  <Num n="03" tone="text-brandpink" />
                  <Scissors className="h-4 w-4 text-brandpink" />
                </div>
                <h3 className="font-display mt-3 text-[1.45rem] font-bold text-white">Instrument sharpening</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-white/75">
                  Scalers, curettes and surgical instruments re-edged to factory sharpness. Call for current turnaround.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <div
              data-testid="service-card-sales"
              className="group relative grid min-h-[300px] grid-cols-1 overflow-hidden rounded-[22px] border border-ink/10 bg-white shadow-[0_6px_24px_rgba(22,50,79,0.06)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_56px_rgba(22,50,79,0.14)] sm:grid-cols-2"
            >
              <div className="flex flex-col justify-between p-7 sm:p-8">
                <div>
                  <div className="flex items-center gap-3">
                    <Num n="04" tone="text-brandleaf-deep" />
                    <ShoppingCart className="h-4 w-4 text-brandleaf-deep" />
                  </div>
                  <h3 className="font-display mt-3 text-[1.45rem] font-bold text-ink">Sales — new & refurbished</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">
                    Handpieces and dental equipment matched to your operatory and your budget. Try before you buy on
                    van days.
                  </p>
                </div>
                <p className="mt-6 inline-flex items-center gap-2 text-[13px] font-bold text-brandleaf-deep">
                  <Phone className="h-4 w-4" /> Ask what's in stock
                </p>
              </div>
              <div className="relative min-h-[220px]">
                <img
                  src={IMAGES.instruments}
                  alt="Dental instruments and handpieces for sale"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-y-0 left-0 hidden w-8 bg-gradient-to-r from-white to-transparent sm:block" />
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="service-card-pickup"
            className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-[22px] border border-ink/10 bg-white px-7 py-5"
          >
            <div className="flex items-center gap-4">
              <span className="rounded-2xl bg-brandleaf-soft p-2.5">
                <Truck className="h-5 w-5 text-brandleaf-deep" />
              </span>
              <p className="text-[14.5px] font-semibold text-ink">
                Free local pickup & delivery around Buffalo/WNY{" "}
                <span className="font-normal text-slate-500">— or ship in from anywhere in the country.</span>
              </p>
            </div>
            <a href="#intake" className="btn-lift inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[13px] font-bold text-white hover:bg-abyss" data-testid="services-intake-cta">
              Book a pickup <span aria-hidden>→</span>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}